import {
  createSettingsParams,
  SettingsParamType,
  ISettingsParam,
} from '@wix/tpa-settings';
import type { Link } from '@wix/document-services-types';
import { DataType } from '@wix/editor-platform-ooi-metadata-resolver';

export type SocialButtonType = 'google' | 'facebook' | 'sso';

interface TextsSettingsParams {
  formTitleText: SettingsParamType.Text;
  formSubtitleText: SettingsParamType.Text;
  formSwitchBarPrefixText: SettingsParamType.Text;
  formSwitchBarTextButtonText: SettingsParamType.Text;
  submitButtonText: SettingsParamType.Text;
  dividerText: SettingsParamType.Text;
  socialButtonsPrefixText: SettingsParamType.Text;
  mandatorySSOSubtitle: SettingsParamType.Text;
}

interface SectionVisibilitySettingsParams {
  showRecaptcha: SettingsParamType.Boolean;
  socialButtonsOrder: SocialButtonType[];
}

interface PrivacyConsentSettingsParams {
  profilePolicyMessageDisplay: SettingsParamType.Text;
  publicProfileCheckedByDefault: SettingsParamType.Boolean;
  termsOfUseLink: Link | undefined;
  privacyPolicyLink: Link | undefined;
}

interface LogoSettingsParams {
  logoUrl: SettingsParamType.Text;
  logoAltText: SettingsParamType.Text;
}

export interface SignupSettingsParams
  extends TextsSettingsParams,
    SectionVisibilitySettingsParams,
    LogoSettingsParams,
    PrivacyConsentSettingsParams {}

export const withoutDefaultFallback = (
  settingsParam: ISettingsParam<any, any>,
) => ({
  ...settingsParam,
  getDefaultValue: () => '',
});

export default createSettingsParams<SignupSettingsParams>({
  logoUrl: {
    type: SettingsParamType.Text,
    getDefaultValue: () => '',
    metadata: {
      type: DataType.image,
      defaultValue: {
        type: 'static',
        source: '',
      },
      dataItemManifestData: {
        displayName: 'Logo Image',
      },
      ai: {
        description:
          'User-uploaded logo image displayed at the top of the signup form',
      },
    },
  },
  logoAltText: {
    type: SettingsParamType.Text,
    getDefaultValue: () => 'Logo',
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t('memberAuthentication.uouScreens.signUpForm.logoAlt'),
      },
      dataItemManifestData: {
        displayName: 'Logo Alt Text',
      },
      ai: {
        description: 'Accessibility alt text for the logo image',
      },
    }),
  },
  formTitleText: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t }) =>
      t('memberAuthentication.uouScreens.signUpForm.title'),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t('memberAuthentication.uouScreens.signUpForm.title'),
      },
      dataItemManifestData: {
        displayName: 'Title',
      },
      ai: {
        description: 'Main heading text of the signup form',
      },
    }),
  },
  formSubtitleText: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t }) =>
      t(
        'memberAuthentication.signupForm.settings.textTab.titleSubtitle.formSubtitle.placeholder',
      ),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t(
          'memberAuthentication.signupForm.settings.textTab.titleSubtitle.formSubtitle.placeholder',
        ),
      },
      dataItemManifestData: {
        displayName: 'Subtitle',
      },
      ai: {
        description: 'Secondary descriptive text displayed below the title',
      },
    }),
  },
  mandatorySSOSubtitle: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t }) =>
      t('memberAuthentication.uouScreens.loginForm.mandatorySSOSubtitle'),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t(
          'memberAuthentication.uouScreens.loginForm.mandatorySSOSubtitle',
        ),
      },
      ai: {
        description:
          'Subtitle shown when SSO is the only signup method available',
      },
    }),
  },
  formSwitchBarPrefixText: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t }) =>
      t('memberAuthentication.uouScreens.signUpForm.loginPrompt'),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t('memberAuthentication.uouScreens.signUpForm.loginPrompt'),
      },
      ai: {
        description:
          'Prompt text before the login link, e.g. "Already have an account?"',
      },
    }),
  },
  formSwitchBarTextButtonText: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t }) =>
      t('memberAuthentication.uouScreens.signUpForm.loginLink'),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t('memberAuthentication.uouScreens.signUpForm.loginLink'),
      },
      ai: {
        description:
          'Clickable link text to switch to the login form, e.g. "Log In"',
      },
    }),
  },
  submitButtonText: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t }) =>
      t('memberAuthentication.uouScreens.signUpForm.signUpButton'),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t('memberAuthentication.uouScreens.signUpForm.signUpButton'),
      },
      ai: {
        description: 'Label on the signup submit button',
      },
    }),
  },
  dividerText: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t }) =>
      t('memberAuthentication.uouScreens.signUpForm.dividerText'),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t('memberAuthentication.uouScreens.signUpForm.dividerText'),
      },
      ai: {
        description:
          'Text in the divider between the email form and social signup options',
      },
    }),
  },
  socialButtonsPrefixText: {
    type: SettingsParamType.Text,
    getDefaultValue: () => 'continueWith',
    metadata: {
      type: DataType.textEnum,
      defaultValue: {
        type: 'static',
        value: 'continueWith',
        options: [
          { value: 'continueWith', displayName: 'Continue with' },
          { value: 'signUpWith', displayName: 'Sign up with' },
        ],
      },
      ai: {
        description:
          'Text prefix pattern for social signup buttons. The selected key maps to a translated label like "Continue with Google".',
      },
    },
  },
  showRecaptcha: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ai: {
        description:
          'Whether to display reCAPTCHA bot verification below the form',
      },
    },
  },
  socialButtonsOrder: {
    type: SettingsParamType.Object,
    getDefaultValue: () => ['google', 'facebook', 'sso'],
    metadata: {
      type: DataType.schema,
      defaultValue: {
        type: 'static',
        value: JSON.stringify({
          type: 'array',
          items: { type: 'string', enum: ['google', 'facebook', 'sso'] },
          uniqueItems: true,
        }),
      },
      ai: {
        description:
          'Ordered list of social login providers controlling display order. Values: google, facebook, sso.',
      },
    },
  },
  profilePolicyMessageDisplay: {
    type: SettingsParamType.Text,
    getDefaultValue: () => 'footnote',
    metadata: {
      type: DataType.textEnum,
      defaultValue: {
        type: 'static',
        value: 'footnote',
        options: [
          { value: 'footnote', displayName: 'Footnote' },
          { value: 'checkbox', displayName: 'Checkbox' },
        ],
      },
      ai: {
        description:
          'How the public profile consent is displayed: as a footnote at the bottom or as an interactive checkbox',
      },
    },
  },
  publicProfileCheckedByDefault: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: { type: 'static', value: true },
      ai: {
        description:
          'Default checked state of the public profile consent checkbox',
      },
    },
  },
  termsOfUseLink: {
    type: SettingsParamType.Object,
    getDefaultValue: () => undefined,
    metadata: {
      type: DataType.link,
      defaultValue: {
        type: 'static',
        value: '',
        linkTypes: ['externalLink', 'pageLink'],
      },
      ai: {
        description:
          "Link to the site's Terms of Use page shown in the consent statement",
      },
    },
  },
  privacyPolicyLink: {
    type: SettingsParamType.Object,
    getDefaultValue: () => undefined,
    metadata: {
      type: DataType.link,
      defaultValue: {
        type: 'static',
        value: '',
        linkTypes: ['externalLink', 'pageLink'],
      },
      ai: {
        description:
          "Link to the site's Privacy Policy page shown in the consent statement",
      },
    },
  },
});
